import { useCallback } from 'react';
import { validateFile } from '../../../../utilities/fileUtils';

interface UseFileValidationProps {
    onFilesSelected: (files: File[]) => void;
    onError?: (errorMessage: string) => void;
    errorMessage?: string;
}

interface UseFileValidationReturn {
    handleFiles: (files: File[]) => Promise<void>;
    handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Custom hook for handling file validation and selection
 * 
 * @param onFilesSelected - Callback when valid files are selected
 * @param onError - Callback for error messages
 * @param errorMessage - Error message to display for invalid files
 * @returns Object containing file handling functions
 */
export const useFileValidation = ({
    onFilesSelected,
    onError,
    errorMessage = "Some files were rejected. Please ensure all files are under 20MB and of supported types (.pdf, .docx, .xlsx, .csv, .txt, .ppt, .pptx, .png, .doc, .xml, .jpg/.jpeg, IPC1752A)."
}: UseFileValidationProps): UseFileValidationReturn => {

    const handleFiles = useCallback(async (files: File[]) => {
        // Validate all files asynchronously
        const validationResults = await Promise.all(
            files.map(async (file) => ({
                file,
                isValid: await validateFile(file)
            }))
        );

        const validFiles = validationResults
            .filter(result => result.isValid)
            .map(result => result.file);

        if (validFiles.length !== files.length) {
            if (onError) {
                onError(errorMessage);
            }
        }

        if (validFiles.length > 0) {
            onFilesSelected(validFiles);
        }
    }, [onFilesSelected, onError, errorMessage]);

    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        handleFiles(files);
        // Reset the input value so the same file can be selected again
        event.target.value = '';
    }, [handleFiles]);

    return {
        handleFiles,
        handleFileSelect
    };
};
